import { useState, useEffect, useRef } from "react"
import { Label } from "reactstrap"
import CreatableSelect from "react-select/creatable"
import { getIn } from "formik"

const SearchableSelect = ({
  fetchFn,
  formik,
  name,
  label,
  valueKey = "id",
  labelKey = "name",
  getOptionLabel,
  placeholder = "Type to search...",
  creatable = true,
  required = false,
  disabled = false,
  debounceMs = 400,
  perPage = 20,
  extraParams,
  onChange: onChangeProp,
  onCreateOption: onCreateOptionProp,
  noOptionsMessage,
  actions,
}) => {
  const [inputValue, setInputValue] = useState("")
  const [debouncedValue, setDebouncedValue] = useState("")
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null)

  const fetchFnRef = useRef(fetchFn)
  fetchFnRef.current = fetchFn

  const extraParamsRef = useRef(extraParams)
  extraParamsRef.current = extraParams

  const getOptionLabelRef = useRef(getOptionLabel)
  getOptionLabelRef.current = getOptionLabel

  const labelKeyRef = useRef(labelKey)
  labelKeyRef.current = labelKey

  const formikValue = getIn(formik?.values, name)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue.trim())
    }, debounceMs)
    return () => clearTimeout(handler)
  }, [inputValue, debounceMs])

  useEffect(() => {
    const fn = fetchFnRef.current
    if (!fn) return
    let cancelled = false
    setLoading(true)

    const params = { all: true, per_page: perPage }
    const ep = extraParamsRef.current
    if (ep) Object.assign(params, ep)
    if (debouncedValue) {
      params.search = debouncedValue
    } else if (formikValue) {
      params.search = String(formikValue)
    }

    fn(params)
      .then(res => {
        if (cancelled) return
        const raw = res?.data?.data ?? res?.data ?? res ?? []
        const items = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
        const lk = labelKeyRef.current
        const gl = getOptionLabelRef.current
        setOptions(
          Array.isArray(items)
            ? items.map(item => ({
                label: gl ? gl(item) : item[lk],
                value: item[valueKey],
                raw: item,
              }))
            : []
        )
      })
      .catch(() => {
        if (!cancelled) setOptions([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedValue, perPage, valueKey, extraParams && JSON.stringify(extraParams), formikValue])

  useEffect(() => {
    if (!formikValue) {
      setSelectedOption(null)
      return
    }
    if (typeof formikValue === "object" && formikValue?.value) {
      setSelectedOption(formikValue)
      return
    }
    const found = options.find(opt => String(opt.value) === String(formikValue))
    setSelectedOption(
      found ? found : { value: formikValue, label: formikValue }
    )
  }, [formikValue, options])

  const handleChange = option => {
    setSelectedOption(option)
    formik?.setFieldValue(name, option ? option.value : "")
    onChangeProp?.(option ? option.value : "", option)
  }

  const handleCreate = input => {
    const created = { label: input, value: input, __isNew__: true }
    setOptions(prev => [created, ...prev])
    setSelectedOption(created)
    formik?.setFieldValue(name, input)
    onCreateOptionProp?.(input)
  }

  const error = getIn(formik?.touched, name) && getIn(formik?.errors, name)

  const customStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: disabled ? "#e9ecef" : "#fff",
      borderColor: error ? "red" : state.isFocused ? "#80bdff" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 0.15rem rgba(0,123,255,0.25)" : "none",
    }),
    menu: base => ({ ...base, zIndex: 9999 }),
    input: base => ({ ...base, backgroundColor: "transparent" }),
    singleValue: base => ({ ...base, backgroundColor: "transparent" }),
  }

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        {label && (
          <Label className="mb-0 form-label">
            {label} {required && <span className="text-danger">*</span>}
          </Label>
        )}
        {actions}
      </div>

      <CreatableSelect
        value={selectedOption}
        options={options}
        isClearable
        isSearchable
        isLoading={loading}
        isDisabled={disabled}
        placeholder={placeholder}
        onChange={handleChange}
        onCreateOption={creatable ? handleCreate : undefined}
        onInputChange={setInputValue}
        noOptionsMessage={() =>
          noOptionsMessage
            ? noOptionsMessage(debouncedValue)
            : debouncedValue
              ? "Not found — press Enter to create"
              : "Start typing to search"
        }
        styles={customStyles}
      />

      {error && (
        <div className="text-danger small mt-1">{getIn(formik?.errors, name)}</div>
      )}
    </div>
  )
}

export default SearchableSelect
